import React from 'react';
import { KeyRound, Timer, Fingerprint, ScrollText, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

const EncryptedEmailSection = () => {
  const encryptionFeatures = [
    {
      icon: KeyRound,
      title: 'Perfect Forward Secrecy',
      description: 'New encryption keys are generated for every single session, so a compromised key never exposes past conversations.',
    },
    {
      icon: Timer,
      title: 'Self-Destructing Messages',
      description: 'Set expiration timers on sensitive emails and attachments. Once the clock runs out, the content is permanently erased.',
    },
    {
      icon: Fingerprint,
      title: 'Biometric Authentication',
      description: 'Hardware-level security with WebAuthn and FIDO2 support built in. Verify identity with a touch or a glance.',
    },
    {
      icon: ScrollText,
      title: 'Tamper-Proof Audit Logs',
      description: 'Immutable, cryptographically signed audit trails for enterprise compliance, regulatory reviews, and peace of mind.',
    },
  ];

  return (
    <section data-testid="encrypted-section" className="py-24 sm:py-32 bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,rgba(29,78,216,0.15),transparent)]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-5 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Encryption that never sleeps
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Guardifix wraps every message in multiple layers of cryptographic protection. Your data stays private at rest, in transit, and everywhere in between.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {encryptionFeatures.map((feature, index) => (
            <motion.div
              key={index}
              data-testid={`encryption-card-${index}`}
              className="bg-white/5 backdrop-blur-md rounded-2xl p-7 border border-white/10 hover:border-white/20 transition-colors duration-300 group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className="mb-5">
                <div className="w-11 h-11 bg-emerald-500 rounded-lg flex items-center justify-center group-hover:rotate-3 transition-transform duration-300">
                  <feature.icon className="h-5 w-5 text-white" strokeWidth={2.5} />
                </div>
              </div>

              <h3 className="text-lg font-bold mb-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {feature.title}
              </h3>

              <p className="text-slate-400 text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-14 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-md border border-white/10 rounded-full px-6 py-3">
            <Lock className="h-5 w-5 text-emerald-400" />
            <span className="text-sm font-semibold text-slate-300">AES-256 + RSA-4096 Encryption Standard</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EncryptedEmailSection;
